import {
    PDFArray,
    PDFDocument,
    PDFPage,
    PDFRawStream,
    PDFRef,
    clip,
    closePath,
    decodePDFRawStream,
    endPath,
    lineTo,
    moveTo,
    popGraphicsState,
    pushGraphicsState,
} from 'pdf-lib';

interface PhotoBox {
    x: number;
    y: number;
    width: number;
    height: number;
}

interface StampResumeOptions {
    pdfUrl: string;
    photoUrl: string;
    fileName?: string;
}

const FALLBACK_BOX: PhotoBox = { x: 473, y: 545, width: 62, height: 85 };

const decodeStream = (stream: PDFRawStream): string => (
    new TextDecoder('latin1').decode(decodePDFRawStream(stream).decode())
);

const readPageContent = (page: PDFPage): string => {
    const contents = page.node.Contents();
    if (!contents) return '';
    if (contents instanceof PDFRawStream) return decodeStream(contents);
    if (!(contents instanceof PDFArray)) return '';

    return contents.asArray().map((item) => {
        const stream = item instanceof PDFRawStream
            ? item
            : item instanceof PDFRef
                ? page.doc.context.lookup(item)
                : null;
        return stream instanceof PDFRawStream ? decodeStream(stream) : '';
    }).join('\n');
};

const matchAll = (value: string, pattern: RegExp): RegExpExecArray[] => {
    const matches: RegExpExecArray[] = [];
    const regex = new RegExp(pattern.source, pattern.flags.includes('g') ? pattern.flags : `${pattern.flags}g`);
    let current: RegExpExecArray | null;
    while ((current = regex.exec(value))) matches.push(current);
    return matches;
};

const findPhotoBox = (page: PDFPage): PhotoBox => {
    const { width: pageWidth, height: pageHeight } = page.getSize();
    const content = readPageContent(page);
    const pathStart = /([\d.]+)\s+([\d.]+)\s+m\s+([\d.]+)\s+([\d.]+)\s+l/g;
    let match: RegExpExecArray | null;

    while ((match = pathStart.exec(content))) {
        const x1 = Number(match[1]);
        const y1 = Number(match[2]);
        const x2 = Number(match[3]);
        const y2 = Number(match[4]);
        const snippet = content.slice(match.index, match.index + 220);
        const points = matchAll(snippet, /([\d.]+)\s+([\d.]+)\s+(?:m|l)\b/g)
            .map((point) => ({ x: Number(point[1]), y: Number(point[2]) }));

        if (points.length < 3) continue;

        const xs = points.map((point) => point.x);
        const ys = points.map((point) => point.y);
        const x = Math.min(...xs);
        const y = Math.min(...ys);
        const width = Math.max(...xs) - x;
        const height = Math.max(...ys) - y;
        const isPortraitBox = width >= 45 && width <= 95 && height >= 60 && height <= 120;
        const isTopRight = x > pageWidth * 0.65 && y > pageHeight * 0.55 && Math.abs(y2 - y1) < 1 && x2 > x1;

        if (isPortraitBox && isTopRight) {
            return { x, y, width, height };
        }
    }

    return FALLBACK_BOX;
};

const embedPhoto = async (pdf: PDFDocument, bytes: ArrayBuffer) => {
    try {
        return await pdf.embedPng(bytes);
    } catch {
        return pdf.embedJpg(bytes);
    }
};

export const stampResumePhoto = async ({ pdfUrl, photoUrl }: StampResumeOptions): Promise<string> => {
    const [pdfResponse, photoResponse] = await Promise.all([
        fetch(pdfUrl),
        fetch(photoUrl),
    ]);

    if (!pdfResponse.ok) throw new Error('No se pudo leer el PDF');
    if (!photoResponse.ok) throw new Error('No se pudo leer la foto');

    const pdf = await PDFDocument.load(await pdfResponse.arrayBuffer());
    const image = await embedPhoto(pdf, await photoResponse.arrayBuffer());
    const page = pdf.getPages()[0];
    const box = findPhotoBox(page);
    const inset = 1.2;
    const frame = {
        x: box.x + inset,
        y: box.y + inset,
        width: box.width - inset * 2,
        height: box.height - inset * 2,
    };

    const scale = Math.max(frame.width / image.width, frame.height / image.height);
    const drawWidth = image.width * scale;
    const drawHeight = image.height * scale;
    const drawX = frame.x + (frame.width - drawWidth) / 2;
    const drawY = frame.y + (frame.height - drawHeight) / 2;

    page.pushOperators(
        pushGraphicsState(),
        moveTo(frame.x, frame.y),
        lineTo(frame.x + frame.width, frame.y),
        lineTo(frame.x + frame.width, frame.y + frame.height),
        lineTo(frame.x, frame.y + frame.height),
        closePath(),
        clip(),
        endPath(),
    );
    page.drawImage(image, {
        x: drawX,
        y: drawY,
        width: drawWidth,
        height: drawHeight,
    });
    page.pushOperators(popGraphicsState());

    const stamped = await pdf.save();
    const bytes = new Uint8Array(stamped.byteLength);
    bytes.set(stamped);
    return URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));
};

export const openStampedResume = async (options: StampResumeOptions): Promise<void> => {
    const url = await stampResumePhoto(options);
    const preview = window.open('about:blank', '_blank');

    if (!preview) return;

    preview.document.title = options.fileName || 'Curriculum - Encuentra Empleo';
    preview.document.body.style.margin = '0';
    preview.document.body.style.background = '#323639';

    const frame = preview.document.createElement('iframe');
    frame.src = url;
    frame.title = preview.document.title;
    frame.setAttribute('style', 'border:0;width:100%;height:100vh;');
    preview.document.body.appendChild(frame);
};
